import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { BriefcaseBusiness, CalendarDays, ChartNoAxesCombined, CheckSquare2, ClipboardList, CornerDownLeft, FileCheck2, KanbanSquare, LayoutDashboard, Search, Settings, UserRound, UsersRound } from "lucide-react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { t } from "@/lib/i18n";
import { hasPermission, type PermissionKey } from "@shared/permissions";
import type { LookupData, User } from "@/types";

type Entry = {
  id: string;
  label: string;
  hint?: string;
  href: string;
  group: "pages" | "candidates";
  icon: typeof Search;
};

const normalize = (value: string) => value.toLowerCase().trim();

/**
 * Cmd/Ctrl+K palette. Jumping between eleven sections used to require a trip to
 * the sidebar; finding a driver required loading the list first. Both are one
 * keystroke away here, and candidates are matched client-side from the lookup
 * payload the app already caches.
 */
export function CommandPalette({ open, onOpenChange, user }: { open: boolean; onOpenChange: (value: boolean) => void; user: User }) {
  const [, navigate] = useLocation();
  const [query, setQuery] = useState("");
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  // Focus returns to the trigger (or whatever had focus) when the palette
  // closes — it used to drop onto <body>, scattering keyboard users.
  const restoreFocusRef = useRef<HTMLElement | null>(null);

  const lookups = useQuery<LookupData>({ queryKey: ["lookups"], queryFn: () => api("/api/lookups"), staleTime: 60_000, enabled: open });

  const entries = useMemo<Entry[]>(() => {
    const pages: { label: string; href: string; icon: typeof Search; permission?: PermissionKey }[] = [
      { label: t("nav_dashboard"), href: "/dashboard", icon: LayoutDashboard, permission: "view" },
      { label: t("nav_hiring"), href: "/hiring", icon: BriefcaseBusiness, permission: "view" },
      { label: t("nav_candidates"), href: "/candidates", icon: UsersRound, permission: "view" },
      { label: t("nav_hiring_pipeline"), href: "/kanban", icon: KanbanSquare, permission: "view" },
      { label: t("nav_interviews"), href: "/interviews", icon: CalendarDays, permission: "manage_interviews" },
      { label: t("nav_tasks"), href: "/tasks", icon: CheckSquare2, permission: "manage_tasks" },
      { label: t("nav_documents"), href: "/documents", icon: FileCheck2, permission: "upload_documents" },
      { label: t("nav_reports"), href: "/reports", icon: ChartNoAxesCombined, permission: "view_reports" },
      { label: t("nav_employees"), href: "/employees", icon: UserRound, permission: "manage_users" },
      { label: t("nav_settings"), href: "/settings", icon: Settings, permission: "manage_settings" },
      { label: t("nav_audit_log"), href: "/audit", icon: ClipboardList },
    ];
    const term = normalize(query);
    const allowed = pages.filter((page) => !page.permission || hasPermission(user.permissions, page.permission));
    const pageEntries: Entry[] = allowed
      .filter((page) => !term || normalize(page.label).includes(term))
      .map((page) => ({ id: `page-${page.href}`, label: page.label, href: page.href, group: "pages", icon: page.icon }));

    const candidateEntries: Entry[] = term.length < 2 ? [] : (lookups.data?.candidates ?? [])
      .filter((candidate) => normalize(candidate.fullName).includes(term))
      .slice(0, 7)
      .map((candidate) => ({ id: `candidate-${candidate.id}`, label: candidate.fullName, href: `/candidates/${candidate.id}`, group: "candidates", icon: UsersRound }));

    return [...pageEntries, ...candidateEntries];
  }, [query, lookups.data, user.permissions]);

  useEffect(() => { setCursor(0); }, [query]);

  useEffect(() => {
    if (!open) {
      setQuery("");
      setCursor(0);
      restoreFocusRef.current?.focus();
      restoreFocusRef.current = null;
      return;
    }
    restoreFocusRef.current = document.activeElement as HTMLElement | null;
    const frame = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    listRef.current?.querySelector<HTMLElement>('[data-active="true"]')?.scrollIntoView({ block: "nearest" });
  }, [cursor, open]);

  if (!open) return null;

  const go = (entry?: Entry) => {
    if (!entry) return;
    onOpenChange(false);
    navigate(entry.href);
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "ArrowDown") { event.preventDefault(); setCursor((value) => (value + 1) % Math.max(entries.length, 1)); }
    if (event.key === "ArrowUp") { event.preventDefault(); setCursor((value) => (value - 1 + entries.length) % Math.max(entries.length, 1)); }
    if (event.key === "Enter") { event.preventDefault(); go(entries[cursor]); }
    if (event.key === "Escape") { event.preventDefault(); event.stopPropagation(); onOpenChange(false); }
    if (event.key === "Tab") {
      // Minimal focus trap: the palette is the only interactive surface while
      // open; Tab cycles between the input and the result list.
      event.preventDefault();
      const focusables = panelRef.current?.querySelectorAll<HTMLElement>("input, button:not([disabled])") ?? [];
      if (!focusables.length) return;
      const index = Array.prototype.indexOf.call(focusables, document.activeElement);
      const next = event.shiftKey ? (index <= 0 ? focusables.length - 1 : index - 1) : (index === focusables.length - 1 ? 0 : index + 1);
      focusables[next]?.focus();
    }
  };

  let lastGroup: Entry["group"] | null = null;

  return <div className="command-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onOpenChange(false); }}>
    <div className="command-panel" role="dialog" aria-modal="true" aria-label={t("command_placeholder")} ref={panelRef}>
      <div className="command-input">
        <Search className="ui-icon"/>
        <input
          ref={inputRef}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={onKeyDown}
          placeholder={t("command_placeholder")}
          aria-label={t("command_placeholder")}
          autoComplete="off"
          role="combobox"
          aria-expanded="true"
          aria-controls="command-results"
          aria-activedescendant={entries[cursor]?.id}
        />
        <kbd>esc</kbd>
      </div>
      <div className="command-results" id="command-results" role="listbox" ref={listRef} onKeyDown={onKeyDown}>
        {lookups.isFetching && !lookups.data ? (
          <div className="command-empty">{t("loading")}</div>
        ) : null}
        {entries.map((entry, index) => {
          const header = entry.group !== lastGroup ? entry.group : null;
          lastGroup = entry.group;
          const Icon = entry.icon;
          return <div key={entry.id}>
            {header ? <div className="command-group">{header === "pages" ? t("command_pages") : t("command_candidates")}</div> : null}
            <button
              id={entry.id}
              type="button"
              role="option"
              aria-selected={index === cursor}
              data-active={index === cursor}
              className={cn("command-item", index === cursor && "active")}
              onMouseMove={() => setCursor(index)}
              onClick={() => go(entry)}
            >
              <span className="command-item-icon"><Icon className="ui-icon"/></span>
              <span className="command-item-label">{entry.label}</span>
              {index === cursor ? <CornerDownLeft className="ui-icon command-item-enter"/> : null}
            </button>
          </div>;
        })}
        {!entries.length && !(lookups.isFetching && !lookups.data) ? <div className="command-empty">{t("command_no_results")}</div> : null}
      </div>
      <div className="command-foot">
        <span><kbd>↑</kbd><kbd>↓</kbd> {t("command_pages")}</span>
        <span><kbd>↵</kbd> {t("command_hint_open")}</span>
        <span><kbd>esc</kbd> {t("command_hint_close")}</span>
      </div>
    </div>
  </div>;
}
